import type { PropsWithChildren } from "react";
import { useEffect } from "react";

import Animated, {
  Easing,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withSequence,
  withTiming,
} from "react-native-reanimated";

// Two phases because Reanimated has no CSS keyframes.
// Phase 1 (60%): entrance -- scale up past full size, fade in, lift.
// Phase 2 (40%): settle -- scale back from 1.07 to 1.0.
enum PopInDuration {
  Phase1 = 492,
  Phase2 = 328,
}

enum PopInDefaults {
  InitialScale = 0.5,
  InitialTranslateY = 10,
  OvershootScale = 1.07,
}

const PopInPhase1Easing = Easing.bezier(0.2, 0.9, 0.3, 1);
const PopInPhase2Easing = Easing.out(Easing.ease);

interface PopInViewProps extends PropsWithChildren {
  delay?: number;
}

/** Pops in from half-size, grows slightly past full size (1.07), then settles to 1. Includes a 10px upward lift. Origin is bottom-center. */
const PopInView = ({ children, delay = 0 }: PopInViewProps) => {
  const isReducedMotion = useReducedMotion();
  const opacity = useSharedValue(isReducedMotion ? 1 : 0);
  const scale = useSharedValue(
    isReducedMotion ? 1 : PopInDefaults.InitialScale,
  );
  const translateY = useSharedValue(
    isReducedMotion ? 0 : PopInDefaults.InitialTranslateY,
  );

  useEffect(() => {
    if (isReducedMotion) return;
    const phase1 = {
      duration: PopInDuration.Phase1,
      easing: PopInPhase1Easing,
    };
    opacity.value = withDelay(delay, withTiming(1, phase1));
    translateY.value = withDelay(delay, withTiming(0, phase1));
    scale.value = withDelay(
      delay,
      withSequence(
        withTiming<number>(PopInDefaults.OvershootScale, phase1),
        withTiming(1, {
          duration: PopInDuration.Phase2,
          easing: PopInPhase2Easing,
        }),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once on mount
  }, []);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ scale: scale.value }, { translateY: translateY.value }],
  }));

  return (
    <Animated.View style={[{ transformOrigin: "50% 92%" }, animatedStyle]}>
      {children}
    </Animated.View>
  );
};

export type { PopInViewProps };
export { PopInView };
